import axios from "axios";
import * as React from "react";
import { lazy, Suspense } from "react";
import type { PageData } from "../../js/data/pageData.ts";
import { ItemWrapperTypeBase } from "../../js/enums.ts";
import { getAllEnv, getEnv } from "../../js/helpers/env.ts";
import { findElementWithIdInArray, objectClone } from "../../js/helpers/utils.ts";
import type { PageItemData, PageRenderedItemData } from "../../js/page/content.types";
import {
	getRepoObjectWithId,
	preparePageForRender,
	type RenderOptions,
	type RenderStateHelper,
	renderTags,
} from "../../js/render/renderFunctionsData.ts";
import { ItemTypeCustom, type ItemWrapperType, ItemWrapperTypeCustom } from "../enumCustom.ts";
import NewsBox from "../frontend/newsBox.tsx";
import PageBox from "../frontend/pageBox";
import RenderMenuItem from "../frontend/renderMenuItem.tsx";

const Service = lazy(() => import('../frontend/service.tsx'));
const Survey = lazy(() => import('../frontend/survey.tsx'));
const LatestNews = lazy(() => import('../frontend/latestNews.tsx'));
const Contact = lazy(() => import('../form/contactForm.tsx'));
const Cv = lazy(() => import('../form/cvForm.tsx'));
const Callback = lazy(() => import('../form/callbackForm.tsx'));
const MapWithSingleMarker = lazy(() => import('../frontend/mapWithSingleMarker.tsx'));
const MapDelivery = lazy(() => import('../frontend/mapDelivery.tsx'));

export const itemClassDefault = ['h-full']; // TODO: h-full is useful only when section has align-items: stretch

export default function renderItemCustom(item: PageRenderedItemData, indexSection?: number, indexItem?: number) {
	switch (item?.type) {
		case ItemTypeCustom.CONTACT:
			return <Contact/>
		case ItemTypeCustom.NEWSLETTER:
			return <Callback/>
		case ItemTypeCustom.SURVEY:
			return <Survey content={item.content}/>

		case ItemTypeCustom.LATEST:
			return <LatestNews item={item}/>
		case ItemTypeCustom.NEWS:
			return item?.page && <NewsBox news={item.page}/>

		case ItemTypeCustom.PAGEBOX:
			return item?.page ? <PageBox item={item.page}/> : ''
		case ItemTypeCustom.SERVICE:
			return item?.page ? <Service item={item.page} indexSection={indexSection} indexItem={indexItem}/> : ''

		case ItemTypeCustom.MAP:
			return <Suspense fallback=""><MapWithSingleMarker latitude={51.91505453404632} longitude={-1.1405590790320639} className="w-full h-[430px] md:h-[560px]"/></Suspense>
		case ItemTypeCustom.MAP_DELIVERY:
			return <Suspense fallback=""><MapDelivery className="w-full h-[430px] md:h-[560px]"/></Suspense>

		case ItemTypeCustom.MENUITEM:
			return <RenderMenuItem item={item}/>

		case ItemTypeCustom.CV:
			return <Cv/>
	}

	return null;
}

export async function fetchLatestNews(amount: number, renderStateHelper: RenderStateHelper, options: RenderOptions = {}) {
	return axios.get(getEnv('PUBLIC_API_HOST') + '/api/news/' + amount).then((response) => {
		return Promise.all(response.data.map((page: PageData) => {
			return preparePageForRender(page, renderStateHelper, options);
		}));
	});
}

export async function prepareContentItemsForRenderCustom(item: PageItemData, renderStateHelper: RenderStateHelper, options: RenderOptions = {}) {
	switch (item.type) {
		case ItemTypeCustom.NEWS:
		case ItemTypeCustom.PAGEBOX:
		case ItemTypeCustom.SERVICE:
			if (!options?.renderFlat && item.content) {
				if (options.hasOwnProperty('skipReusable') === false) {
					options.skipReusable = [];
				}

				if (options?.skipReusable?.includes(Number.parseInt(item.content)) === false) {
					//console.log("loading item reusable: " + item.content, options);
					item.page = await getRepoObjectWithId('page', Number.parseInt(item.content), renderStateHelper, {
						...objectClone(options),
						renderFlat: ItemTypeCustom.SERVICE === item.type // render services flat, slider and reusable are ok to load fully
					}).then((data) => {
						options.skipReusable.push(Number.parseInt(item.content));
						return data;
					});
				} else {
					console.log('item skipping loading page: ' + item.content);
				}
			}
			break;

		case ItemTypeCustom.LATEST:
			if (!options?.renderFlat) {
				const amount = (Number.parseInt(item.content) ? Number.parseInt(item.content) : 3);
				item.latestNews = await fetchLatestNews(amount, renderStateHelper, {...objectClone(options), renderFlat: true});
			}
			break;

		case ItemTypeCustom.MENUITEM:
			if (!options?.skipState && item.editorState) {
				try {
					const state = renderStateHelper.editor.parseEditorState(item.editorState);
					item.rendered = renderStateHelper.renderEditorState(renderStateHelper.editor, state);
					item.rendered = renderTags(item.rendered, {
						...getAllEnv(),
						...options.tags
					});
				} catch {
					console.info('wrong text item state:');
					console.dir(item.editorState, {depth: 3});
				}
			}

			if (!options?.skipData) {
				if (item.imageId) {
					item.image = await getRepoObjectWithId('asset', item.imageId);
				}
			}
			break;
	}

	return item;
}

export function renderItemWrapperCustom(wrapper: ItemWrapperType, rendered: React.ReactNode) {

	switch (wrapper) {
		case ItemWrapperTypeCustom.BLOCKQUOTE:
			return <blockquote className="md:text-center">
				<div className="stylistic-quote-mark-before" aria-hidden="true"/>
				{rendered}
			</blockquote>;

		case ItemWrapperTypeCustom.HEADER_BOX_BIG:
			return <div className="headerBox big">
				<div>{rendered}</div>
			</div>;

		case ItemWrapperTypeCustom.HEADER_BOX_FULLSCREEN:
			return <div className="headerBox h-screen relative my-0">
				<div>{rendered}</div>
				<div className="absolute bottom-0 inset-x-0">
					<button className="bg-transparent p-0 border-0 text-center text-xs group mb-32 hover:mb-28 transition-all ease-mine" onClick={(event) => {
						event.preventDefault();

						const anchors = [...document.querySelectorAll('.offsetAnchor')];
						const anchorOfSection = event.target.closest('section').previousSibling;
						const anchorIndex = (anchors.indexOf(anchorOfSection));

						if (anchorIndex !== -1 && anchors[anchorIndex + 1] !== undefined) {
							anchors[anchorIndex + 1].scrollIntoView({
								behavior: "smooth"
							});
						}
					}}>
						SCROLL<br/>DOWN<br/>
						<svg width="30" height="54" viewBox="0 0 30 54" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline-block transition-all ease-mine mt-2 group-hover:mt-4">
							<rect x="0.75" y="0.75" width="28.5" height="52.5" rx="14.25" stroke="white" strokeWidth="1.5"/>

							<path fillRule="evenodd" clipRule="evenodd" className="animate-bounce-mine"
							      d="M14.9653 21C15.1726 21 15.3714 21.0823 15.5179 21.2289C15.6645 21.3755 15.7468 21.5742 15.7468 21.7815V40.2144L20.6642 35.2956C20.8109 35.1488 21.01 35.0664 21.2175 35.0664C21.425 35.0664 21.6241 35.1488 21.7708 35.2956C21.9176 35.4423 22 35.6413 22 35.8489C22 36.0564 21.9176 36.2554 21.7708 36.4022L15.5186 42.6544C15.446 42.7271 15.3598 42.7849 15.2649 42.8243C15.1699 42.8637 15.0681 42.884 14.9653 42.884C14.8625 42.884 14.7607 42.8637 14.6658 42.8243C14.5709 42.7849 14.4846 42.7271 14.412 42.6544L8.15985 36.4022C8.01311 36.2554 7.93066 36.0564 7.93066 35.8489C7.93066 35.6413 8.01311 35.4423 8.15985 35.2956C8.3066 35.1488 8.50564 35.0664 8.71317 35.0664C8.9207 35.0664 9.11974 35.1488 9.26649 35.2956L14.1838 40.2144V21.7815C14.1838 21.5742 14.2661 21.3755 14.4127 21.2289C14.5593 21.0823 14.7581 21 14.9653 21Z"
							      fill="white"/>
						</svg>
					</button>
				</div>

			</div>;
	}

	return rendered;
}
